/* eslint-disable @typescript-eslint/no-explicit-any */
'use client';

import React, { useEffect, useState } from 'react';
import { components } from 'react-select';
import { Options } from 'react-select';
import debounce from 'lodash/debounce';
import {
  AddressAutoSearch,
  AddressAutoSearchProps,
} from '@src/interfaces/components/searchAutocomplete';
import { searchProperty } from '@src/services/properties';
import { Popover, Transition } from '@headlessui/react';
import { useWindowSize } from '@react-hook/window-size';
import { isMobile } from '@src/utils/helper';
import AsyncSelect from 'react-select/async';
const SearchAutocomplete: React.FC<AddressAutoSearchProps> = ({
  onSelect,
  defaultInputValue,
  defaultValue = [],
  searchType = '',
  extraClasses,
}) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const selectRef: any = React.useRef(null);
  const [width] = useWindowSize();

  const [isLoading, setIsLoading] = useState(false);
  const [options, setOptions] = useState<
    {
      label: string;
      value: string;
      types: string;
      terms: { offset: number; value: string }[];
    }[]
  >([]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [componentOptions, setComponentOptions] = useState<any>(null);
  const [selectedOptions, setSelectedOptions] =
    useState<AddressAutoSearch[]>(defaultValue);

  const loadOptions = async (
    inputValue: string,
    // eslint-disable-next-line no-unused-vars
    callback: (options: Options<{ label: string; value: string }>) => void
  ): Promise<void> => {
    if (inputValue.length >= 3) {
      setIsLoading(true);

      try {
        let restrict = '';
        if (selectedOptions.length > 0) {
          restrict = selectedOptions[0].types || searchType || '';
        }

        const result = await searchProperty(
          inputValue,
          restrict,
          false,
          defaultValue[0]?.main_text || ''
        );

        if (result && result.length > 0) {
          const options = result.map(
            (item: { data: string; display: string; type: string }) => ({
              label: item.display,
              value: item.data,
              types: item.type,
              terms: '',
            })
          );
          setOptions(options);
          callback(options);
          return;
        }

        setOptions([]);
        callback([]);
      } catch (e) {
        console.error(e);
        callback([]);
      } finally {
        selectRef?.current?.focus();
        setIsLoading(false);
      }
    } else {
      callback([]);
    }
  };

  const debouncedLoadOptions = debounce(
    (
      inputValue: string,
      callback: (
        // eslint-disable-next-line no-unused-vars
        options: Options<{ label: string; value: string }>
      ) => void | Promise<Options<{ label: string; value: string }>>
    ) => {
      loadOptions(inputValue, callback);
    },
    800
  );

  const handleInputChange = (inputValue: string) => {
    debouncedLoadOptions(inputValue, () => {
      return;
    });
  };

  const handleChange = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    selectedOption: any
  ) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const selectedItems = selectedOption.map((option: any) => {
      return {
        address: option?.label || '',
        place_id: option?.value || '',
        main_text: option?.value || '',
        secondary_text: '',
        types: option?.types || searchType || '',
      };
    });

    onSelect(selectedItems);
    setSelectedOptions(selectedItems);
    setOptions([]);
  };

  const MoreSelectedBadge = ({ items }: { items: any }) => {
    const { length = 0 } = items;
    const label = `+ ${length - 1} item${length - 1 !== 1 ? 's' : ''}`;

    return (
      <Popover className="relative z-50">
        <Popover.Button
          className="bg-primary-dark/20 rounded text-11 p-1 order-[0] whitespace-nowrap"
          onClick={(e) => e.stopPropagation()}
          title={label}
        >
          {label}
        </Popover.Button>

        <Transition
          enter="transition duration-100 ease-out"
          enterFrom="transform scale-95 opacity-0"
          enterTo="transform scale-100 opacity-100"
          leave="transition duration-75 ease-out"
          leaveFrom="transform scale-100 opacity-100"
          leaveTo="transform scale-95 opacity-0"
        >
          <Popover.Panel className="absolute xs:inset-auto xs:absolute xs:left-1/2 z-50 xs:m-2 md:m-0 md:mt-2 w-[calc(100%-32px)] xs:h-auto h-auto xs:w-screen xs:max-w-max lg:-translate-x-[10%] md:-translate-x-[10%] sm:-translate-x-[17%] xs:-translate-x-[15%]">
            <div className="w-full min-w-[200px] max-w-[300px] rounded-xl bg-white text-sm leading-6 drop-shadow-container p-2  max-h-[200px] overflow-y-auto">
              <div className="text-11 font-medium mb-2">Your Selection</div>
              <div className="flex flex-wrap">
                {(items || [])?.slice(1)?.map((item: any) => item)}
              </div>
            </div>
          </Popover.Panel>
        </Transition>
      </Popover>
    );
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const CustomControl = (props: any) => {
    return <components.Control {...props} innerProps={{}}></components.Control>;
  };

  const CustomValueContainer = ({ children, ...props }: any) => {
    const [values, input] = children;

    const maxToShow = 1;

    return (
      <components.ValueContainer
        {...props}
        onTouchStart={(event: React.TouchEvent) => {
          event.stopPropagation();
        }}
      >
        {/* display if only one value is selected */}
        {props.hasValue && Number(values?.length) === maxToShow
          ? values[0]
          : null}

        {/* Display single value and +{Items.length} block*/}
        {props.hasValue && Number(values?.length) > maxToShow ? (
          <>
            {values[0]}
            <MoreSelectedBadge items={values} />
          </>
        ) : null}

        {/* Only display placeholder element */}
        {!props.hasValue ? values : null}

        {/* input field */}
        {input}
      </components.ValueContainer>
    );
  };

  useEffect(() => {
    setComponentOptions(
      isMobile() && width && width <= 768
        ? { ValueContainer: CustomValueContainer, Control: CustomControl }
        : {
            ValueContainer: CustomValueContainer,
          }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AsyncSelect
      isMulti
      defaultValue={(defaultValue || []).map((singleValue) => {
        return {
          label: singleValue?.address || '',
          value: singleValue?.main_text || '',
        };
      })}
      ref={selectRef}
      loadOptions={debouncedLoadOptions}
      onInputChange={handleInputChange}
      onChange={handleChange}
      placeholder="Search by address, neighborhood, state, zip"
      isLoading={isLoading}
      loadingMessage={() => 'Loading...'}
      options={options}
      isClearable={true}
      defaultInputValue={defaultInputValue}
      className={`zm-select-wrap zm-address-select ${extraClasses}`}
      classNamePrefix="zm-select"
      components={componentOptions}
      isSearchable={
        selectedOptions.length === 1 && searchType === 'exact_address'
          ? false
          : true
      }
      instanceId={`search-autocomplete`}
    />
  );
};

export default SearchAutocomplete;
